import React, { useEffect, useState } from 'react';
import { Menu } from '@headlessui/react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';

import { Pagination, ListNoData, ErrorLoading } from '../../../../../../../../../../components/common';
import PopoverWithFloat from '../../../../../../../../../../components/hoc/PopoverWithFloat';
import { ListLoader } from '../../../../../../../../../../components/loaders';
import transactionsService from '../../../../../../../../../../services/api/payments/transactionsService';
import useAlert from '../../../../../../../../../../state/hooks/useAlert';
import usePagedListApi from '../../../../../../../../../../state/hooks/usePagedListApi';
import useToast from '../../../../../../../../../../state/hooks/useToast';
import { paginationInfo } from '../../../../../../../../../../utils/pagination';
import ModalReversePaymentTransaction from '../../../modals/ModalReversePaymentTransaction';
import ModalTransactionDetails from '../../../modals/ModalTransactionDetails';
import ModalInvoiceDetails from '../../../modals/ModalInvoiceDetails';
import ModalSikaIDPinForm from '../../../modals/ModalSikaIDPinForm';
import useAuth from '../../../../../../../../../../state/hooks/useAuth';


function WalletTransactionsListSection() {
    const { profile, walletTransactionsList, customerProfile }: any = useOutletContext();
    const { hasPermission } = useAuth();

    //const { data: transactions, requestData, resetRecords, updateRecord } = usePagedListApi((query: any) => transactionsService.getCustomerTransactionsByReference(profile.id, query));
    const { data: transactions, requestData, resetRecords, updateRecord } = walletTransactionsList;
    const [searchParams, setSearchParams] = useSearchParams();

    const { addToast } = useToast();

    const [transactionDetails, setTransactionDetails] = useState({ open: false, transaction: null });
    const [invoiceDetails, setInvoiceDetails] = useState({ open: false, invoice: null });
    const [reverseTransactionForm, setReverseTransactionForm] = useState({ open: false, transaction: null });
    const [sikaIdPinFormOpen, setSikaIdPinFormOpen] = useState(false);

    // get query params
    const page = Number(searchParams.get('page')) || 1;
    const searchFilters: any = {};
    // if (searchParams.get('name')) { searchFilters['name'] = searchParams.get('name') || '' }


    useEffect(() => {
        getTransactions();
    }, [searchParams]);


    const getTransactions = async () => {
        await requestData({
            page: page,
            filters: searchFilters,
            otherProperties: {
                pageSize: 10,
                properties: "datetime",
                direction: "DESC",
                walletOnly: true,
            },
        });
    };


    return (
        <div className=''>
            <div className='flex justify-between flex-wrap gap-y-2 gapx-4 mb-2'>
                <h2 className='text-xl font-medium'>SikaID History</h2>


                {
                    hasPermission("CREATE_PIN") && customerProfile?.accounts[0] &&
                    <Menu as="div" className="relative inline-block text-left">
                        {
                            ({ open }) => (
                                <PopoverWithFloat placement='bottom-end' portal>
                                    <Menu.Button type='button' className={`cursor-pointer flex items-center gap-x-2 transition-colors duration-300 bg-black/5 hover:bg-black/15 dark:bg-white/10 dark:hover:bg-white/15 rounded-lg py-2.5 px-4`}>
                                        <span>Action</span>
                                        <i className="ri-arrow-down-s-line opacity-70"></i>
                                    </Menu.Button>

                                    <Menu.Items as='div' unmount={false} className="w-56 right-0 origin-top-right rounded-xl bg-white dark:bg-gray-900 shadow-xl dark:shadow-gray-700/60 overflow-hidden px-2 py-3">
                                        <Menu.Item
                                            as="button"
                                            type='button'
                                            className="w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg px-4 py-3"
                                            onClick={() => setSikaIdPinFormOpen(true)}
                                        >
                                            Create SikaID Pin
                                        </Menu.Item>
                                    </Menu.Items>
                                </PopoverWithFloat>
                            )
                        }
                    </Menu>
                }
            </div>

            {
                // if there're transactions
                !transactions.loadingError && !transactions.loading && transactions.list.length > 0 &&
                <React.Fragment>
                    <div className='max-w-full overflow-x-auto mb-6'>
                        <table className="border-collapse table-auto align-top w-full">
                            <thead>
                                <tr className='text-left border-b-2 dark:border-gray-700'>
                                    <th className='py-6 pl-2 pr-6'>Transaction</th>
                                    <th className='py-6 px-6'>Amount</th>
                                    <th className='py-6 px-6'>Category</th>
                                    <th className='py-6 px-6'>Channel</th>
                                    <th className='py-6 px-6'>Status</th>
                                    <th className='py-6 px-6'>Date</th>
                                    <th className='py-6 pl-6 pr-2 text-right'>Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y dark:divide-gray-700/60">
                                {
                                    transactions.list.map((transactionItem: any) =>
                                        <tr key={transactionItem?.id}>
                                            <td className='py-4 pl-2 pr-6'>
                                                <button type='button' onClick={() => setTransactionDetails({ open: true, transaction: transactionItem })} className='flex gap-3 items-center hover:text-blue'>
                                                    {
                                                        transactionItem.type === "TOP_UP" &&
                                                        <React.Fragment>
                                                            <span className='w-9 h-9 text-2xl flex justify-center items-center rounded-full bg-green-500/10 text-green-500 '><i className="ri-arrow-up-line"></i></span>
                                                            <span>Top Up</span>
                                                        </React.Fragment>
                                                    }
                                                    {
                                                        transactionItem.type === "REDUCE_BALANCE" &&
                                                        <React.Fragment>
                                                            <span className='w-9 h-9 text-2xl flex justify-center items-center rounded-full bg-red-500/10 text-red-500 '><i className="ri-arrow-down-line"></i></span>
                                                            <span>Redeem</span>
                                                        </React.Fragment>
                                                    }
                                                    {
                                                        transactionItem.type !== "REDUCE_BALANCE" && transactionItem.type !== "TOP_UP" &&
                                                        <React.Fragment>
                                                            <span className='w-9 h-9 text-2xl flex justify-center items-center rounded-full bg-blue-500/10 text-blue-500 '><i className="ri-subtract-line"></i></span>
                                                            <span>General</span>
                                                        </React.Fragment>
                                                    }
                                                </button>
                                            </td>
                                            <td className='whitespace-nowrap py-4 px-6'>GHS {transactionItem.amountProcessed.toFixed(2)}</td>
                                            <td className='py-4 px-6'>{transactionItem.invoice?.name}</td>
                                            <td className='py-4 px-6'>{transactionItem.paymentChannel}</td>
                                            <td className='py-4 px-6'>{transactionItem.status}</td>
                                            <td className='py-4 px-6 whitespace-nowrap'>{format(new Date(transactionItem?.datetime), 'd LLL yyyy h:mm a')}</td>
                                            <td className='py-4 pl-6 pr-2 text-right'>
                                                <Menu as="div" className="relative inline-block text-left">
                                                    <PopoverWithFloat placement='bottom-end' portal>
                                                        <Menu.Button type='button' className={`w-9 h-9 transition-colors duration-300 bg-opacity-0 rounded-full hover:bg-black/5 dark:hover:bg-white/5 ui-open:bg-black/5 dark:ui-open:bg-white/5`}>
                                                            <i className="ri-more-2-fill"></i>
                                                        </Menu.Button>

                                                        <Menu.Items as='div' unmount={false} className="w-60 right-0 origin-top-right rounded-xl bg-white dark:bg-gray-900 shadow-xl dark:shadow-gray-700/60 overflow-hidden px-2 py-3">
                                                            <Menu.Item as="button" type='button' onClick={() => setTransactionDetails({ open: true, transaction: transactionItem })} className="w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg px-4 py-3">
                                                                View Details
                                                            </Menu.Item>
                                                            <Menu.Item as="button" type='button' onClick={() => setInvoiceDetails({ open: true, invoice: transactionItem.invoice })} className="w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg px-4 py-3">
                                                                View Invoice
                                                            </Menu.Item>
                                                            {
                                                                transactionItem.status !== "REVERSED" && (transactionItem.paymentChannel === "CASH" || transactionItem.paymentChannel === "OFFLINE") &&
                                                                <Menu.Item as="button" type='button' onClick={() => setReverseTransactionForm({ open: true, transaction: transactionItem })} className="w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg px-4 py-3">
                                                                    Reverse Transaction
                                                                </Menu.Item>
                                                            }
                                                        </Menu.Items>
                                                    </PopoverWithFloat>
                                                </Menu>
                                            </td>
                                        </tr>
                                    )
                                }
                            </tbody>
                        </table>
                    </div>

                    <div className='flex justify-between flex-wrap'>
                        <p className='mb-3 mr-8 text-gray-500 dark:text-gray-400'>{paginationInfo(transactions.pager.pageSize, transactions.pager.totalCount, page)}</p>

                        <Pagination
                            currentPage={page}
                            pageSize={transactions.pager.pageSize}
                            totalCount={transactions.pager.totalCount}
                            onPageChange={(page) => {
                                setSearchParams(current => ({
                                    ...Object.fromEntries(current.entries()),
                                    page: page.toString(),
                                }));
                            }}
                        />
                    </div>
                </React.Fragment>
            }

            {
                // if transactions are loading
                !transactions.loadingError && transactions.loading &&
                <ListLoader loadingText='Loading History' className='px-8 py-24' />
            }

            {
                // if there're no transactions on the student's account
                !transactions.loadingError && !transactions.loading && transactions.list.length === 0 && Object.keys(searchFilters).length === 0 &&
                <ListNoData title='No History Found' description='Student is yet to make a transaction on wallet' className='px-8 py-24'>
                </ListNoData>
            }

            {
                // if there're no transaction found in search
                !transactions.loadingError && !transactions.loading && transactions.list.length === 0 && Object.keys(searchFilters).length > 0 &&
                <ListNoData title='No History Found' description='No history found for the selected scenarios' className='px-8 py-24' />
            }


            {
                transactions.loadingError &&
                <ErrorLoading title='Error Loading History' message='There was a problem loading wallet history' className='px-8 py-24' onTryAgain={getTransactions} />
            }


            <ModalTransactionDetails
                isOpen={transactionDetails.open}
                transaction={transactionDetails.transaction}
                onClose={() => setTransactionDetails((prevState) => ({ ...prevState, open: false }))}
            />

            <ModalReversePaymentTransaction
                isOpen={reverseTransactionForm.open}
                transaction={reverseTransactionForm.transaction}
                onClose={() => setReverseTransactionForm((prevState) => ({ ...prevState, open: false }))}
                onTransactionReversed={(transaction: any) => {
                    updateRecord({ item: transaction, page });
                    addToast({
                        text: `Transaction reversed successfully`,
                        type: "success"
                    });
                }}
            />

            <ModalInvoiceDetails
                isOpen={invoiceDetails.open}
                invoice={invoiceDetails.invoice}
                onClose={() => setInvoiceDetails((prevState) => ({ ...prevState, open: false }))}
            />

            <ModalSikaIDPinForm
                account={customerProfile?.accounts[0]}
                isOpen={sikaIdPinFormOpen}
                onClose={() => setSikaIdPinFormOpen(false)}
                onPinChanged={() => {
                    addToast({
                        text: 'Pin Created Successfully',
                        type: 'success'
                    })
                }}
            />
        </div>
    );
}

export default WalletTransactionsListSection;